import type { ReactElement } from 'react'
import { extractIdsFromIris } from '~/api/utils'
import type { DropdownOption } from '~/components/Form/Input/Dropdown'
import { AddressBookPicker } from '~/domains/actions/components/AddressBookPicker'
import { useApplicantsHead } from '~/domains/applicants/queries/applicantHead'
import { useContactsHead } from '~/domains/contacts/queries/useContactHead'
import { parseIdList } from '~/utils/lists'
import { formatName } from '~/utils/names'

type PersonsFilterEditorProps = {
  type: 'applicant' | 'contact'
  /** Comma separated ids */
  value: string
  placeholder: string
  onChange: (value: string) => void
}

const resources = { applicant: 'applicants', contact: 'contacts' } as const

/** Applicant or contact filter editor: the address book search, values kept as ids. */
export const PersonsFilterEditor = ({
  type,
  value,
  placeholder,
  onChange
}: PersonsFilterEditorProps): ReactElement | null => {
  const ids = parseIdList(value)
  const resource = resources[type]
  const applicantsHead = useApplicantsHead({
    applicantIds: type === 'applicant' ? ids : []
  })
  const contactsHead = useContactsHead({
    contactIds: type === 'contact' ? ids : []
  })
  const heads = type === 'applicant' ? applicantsHead : contactsHead

  // The picker seeds the labels of its selected values on mount
  if (heads.some((query) => query.isPending)) {
    return null
  }
  const selectedOptions: DropdownOption[] = heads.flatMap(({ data }) =>
    data
      ? [{ value: `/v1/${resource}/${data.id}`, label: formatName(data) }]
      : []
  )

  return (
    <AddressBookPicker
      name={resource}
      types={[type]}
      placeholder={placeholder}
      defaultValueOptions={selectedOptions}
      inlineList
      small
      fullWidth
      onChange={(iris) =>
        onChange(extractIdsFromIris(iris, resource).join(','))
      }
    />
  )
}
